import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus } from 'lucide-react';
import { CommonTable } from '../../components/CommonTable';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
import AddBrandForm from './AddBrandForm';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';
import {
  setPage,
  setPageSize,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  fetchBrandsThunk,
} from '../../features/partnerBrands/partnerBrandSlice';

import { BrandDetailTabs } from './BrandDetailTabs';
import {
  ExtendedBrand,
  FetchResult,
  BrandFormData,
  validateForm
} from './BrandTypes';
import { getBrandColumns } from './BrandTable';
import * as handlers from './BrandHandlers';
import { validateCustomerSearchInput } from "../../utils/searchValidator";

const ManageBrands = () => {
  // Get current user role to determine permissions
  const { user, token: reduxToken } = useAppSelector(state => state.auth);

  const dispatch = useAppDispatch();

  const {
    brands,
    loading,
    page,
    pageSize,
    total,
    sortBy,
    sortOrder,
    searchQuery,
    hasFetched,
  } = useAppSelector(state => state.partnerBrand);

  const currentUserRole = user?.role || UserRole.CLEVERPE_USER;

  // Check if user can edit/delete Brands (needs ADMIN/SUPERADMIN role AND manage-brand permission)
  const canManageBrands = useMemo(() => {
    // Check if user has the specific manage-brand permission
    const hasManageBrandsPermission = user?.dashPermissions?.includes('manage-brand') || false;

    // Only check for the permission, regardless of user role
    return hasManageBrandsPermission;
  }, [user?.dashPermissions]);

  // Table state
  const [error, setError] = useState<string | null>(null);
  const [isClientPaginated, setIsClientPaginated] = useState(false);

  // Modal state
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<ExtendedBrand | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<BrandFormData | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeDetailsTab, setActiveDetailsTab] = useState<'basic' | 'bank' | 'shop' | 'docs' | 'logs'>('basic');

  // KYC editing state
  const [isBankKycEditing, setIsBankKycEditing] = useState(false);
  const [isShopKycEditing, setIsShopKycEditing] = useState(false);
  const [isDocsKycEditing, setIsDocsKycEditing] = useState(false);
  const [bankKycData, setBankKycData] = useState<{ accountNo: string; holderName: string; ifsc: string } | null>(null);
  const [shopKycData, setShopKycData] = useState<{ name: string; address: string; state: string; city: string; pin: string; gstNo?: string } | null>(null);
  const [docsKycFiles, setDocsKycFiles] = useState<{ shopFront: File | null; shopInner: File | null; evCer: File | null }>({
    shopFront: null,
    shopInner: null,
    evCer: null
  });

  const [localSearch, setLocalSearch] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchValid, setIsSearchValid] = useState(false);
  const handleSearchInputChange = (value: string) => {
    setLocalSearch(value);

    // ✅ When input is cleared
    if (!value.trim()) {
      setSearchError(null);
      setIsSearchValid(true); // allow reset

      // 🔁 Reset table immediately
      dispatch(setPage(1));
      dispatch(setSearchQuery("")); // empty search
      return;
    }

    // Normal validation
    const result = validateCustomerSearchInput(value);
    setIsSearchValid(result.isValid);
    setSearchError(result.message || null);
  };


  const handleSearchSubmit = () => {
    // If empty, already handled in change
    if (!localSearch.trim()) return;

    if (!isSearchValid) return;

    dispatch(setPage(1));
    dispatch(setSearchQuery(localSearch));
  };

  const handleResetSearch = () => {
    setLocalSearch("");    // clears input
    setSearchError(null);  // clears error
    setIsSearchValid(false); // disables search button
  };


  // Check both Redux state and localStorage to ensure we have a token
  const getToken = () => {
    if (reduxToken) {
      return reduxToken;
    }

    const localToken = localStorage.getItem('token');
    if (localToken) {
      return localToken;
    }

    console.warn("No token found in Redux state or localStorage");
    return null;
  };

  const { token } = useAppSelector((state) => state.auth);
  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
  });

  // ✅ Only fetch once on first mount
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchBrandsThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);



  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchBrandsThunk());

  }, [token, hasFetched, dispatch]);


  const handleRefresh = () => {
      // 🔹 Clear local search UI state
      setLocalSearch("");
      setSearchError(null);
      setIsSearchValid(false);
  
      // 🔹 Clear redux search
      dispatch(setSearchQuery(""));
  
      // 🔹 Reset pagination
      dispatch(setPage(1));
  
      // 🔹 Fetch fresh data
      dispatch(fetchBrandsThunk());
    };


  // Handler functions
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    handlers.handleChange(e, editData!, setEditData, errors, setErrors);

  const handleValidateForm = () => validateForm(editData!, setErrors);

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));

  };


  const handlePageSizeChange = (newSize: number) => {
    dispatch(setPageSize(newSize));
    dispatch(setPage(1)); // reset page

  };


  const handleSearchChange = (query: string) => {
    dispatch(setSearchQuery(query));
    dispatch(setPage(1)); // reset page

  };


  const handleSortChange = (columnId: string, order: 'asc' | 'desc') => {
    dispatch(setSortBy(columnId));
    dispatch(setSortOrder(order));

  };


  const handleRowClick = (Brand: ExtendedBrand) =>
    handlers.handleRowClick(Brand, setSelectedBrand, setEditData, setIsEditing, setDetailsOpen);

  const handleAddBrand = () => setAddBrandOpen(true);

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setIsEditing(false);
  };

  const handleEditBrand = () => setIsEditing(true);

  const handleDeleteConfirm = () => setDeleteConfirmOpen(true);

  const handleSave = async () =>
    handlers.handleSave(
      handleValidateForm,
      editData,
      selectedBrand,
      setIsUpdating,
      setIsEditing,
      setSelectedBrand,
      async () => {
        if (!getToken()) return;
        try {
          await dispatch(fetchBrandsThunk()).unwrap(); // unwrap converts to actual payload or throws
        } catch (err) {
          console.error("Failed to refresh brands", err);
        }
      },
      getToken
    );

  const handleEditBankKyc = () => {
    if (!selectedBrand) return;
    handlers.handleEditBankKyc(
      selectedBrand,
      setBankKycData,
      setIsBankKycEditing
    );
  };

  const handleBankKycChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handlers.handleBankKycChange(e, bankKycData, setBankKycData, errors, setErrors);

  const saveBankKyc = async () =>
    handlers.saveBankKyc(
      selectedBrand,
      bankKycData,
      errors,
      setErrors,
      setIsUpdating,
      setSelectedBrand,
      setIsBankKycEditing,
      async () => {
        if (!getToken()) return;
        try {
          await dispatch(fetchBrandsThunk()).unwrap();
        } catch (err) {
          console.error("Failed to refresh brands", err);
        }
      },
      getToken
    );


  const handleEditShopKyc = () =>
    handlers.handleEditShopKyc(selectedBrand!, setShopKycData, setIsShopKycEditing);

  const handleShopKycChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    handlers.handleShopKycChange(e, shopKycData, setShopKycData, errors, setErrors);

  const saveShopKyc = async () =>
    handlers.saveShopKyc(
      selectedBrand,
      shopKycData,
      errors,
      setErrors,
      setIsUpdating,
      setSelectedBrand,
      setIsShopKycEditing,
      async () => {
        try {
          await dispatch(fetchBrandsThunk()).unwrap();
        } catch (err) {
          console.error("Failed to refresh brands", err);
        }
      },
      getToken
    );


  const handleEditDocsKyc = () =>
    handlers.handleEditDocsKyc(setDocsKycFiles, setIsDocsKycEditing);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, docType: 'shopFront' | 'shopInner' | 'evCer') =>
    handlers.handleFileChange(e, docType, setDocsKycFiles);

  const saveDocsKyc = async () =>
    handlers.saveDocsKyc(
      selectedBrand,
      docsKycFiles,
      errors,
      setErrors,
      setIsUpdating,
      setSelectedBrand,
      setIsDocsKycEditing,
      async () => {
        try {
          await dispatch(fetchBrandsThunk()).unwrap();
        } catch (e) {
          console.error("Refresh brands failed", e);
        }
      },
      getToken
    );


  const handleDeleteBrand = async () =>
    handlers.handleDeleteBrand(
      selectedBrand,
      setIsDeleting,
      setDeleteConfirmOpen,
      setDetailsOpen,
      async () => {
        try {
          await dispatch(fetchBrandsThunk()).unwrap();
        } catch (e) {
          console.error("Refresh brands failed", e);
        }
      },
      getToken
    );


  const handleCreateBrand = async (formData: any) =>
    handlers.handleCreateBrand(
      formData,
      setAddBrandOpen,
      async () => {
        try {
          await dispatch(fetchBrandsThunk()).unwrap();
        } catch (e) {
          console.error("Refresh brands failed", e);
        }
      },
      getToken
    );


  // Get table columns
  const columns = useMemo(() => getBrandColumns(page, pageSize), [page, pageSize]);

  return (
    <div className="w-full">
      {/* Header section with Add Brand button (only for admin/superadmin) */}
      <div className="flex justify-end items-center mb-4">
        {canManageBrands && (
          <Button
            onClick={handleAddBrand}
            leftIcon={<Plus size={16} />}
            size="sm"
          >
            Add Partner
          </Button>
        )}
      </div>

      {/* Brands Table - Compact view */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <CommonTable
          columns={columns}
          data={brands}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onRefresh={handleRefresh}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          searchValue={localSearch}
          onSearchChange={handleSearchInputChange}
          onSearchSubmit={handleSearchSubmit}
          isSearchDisabled={!isSearchValid}
          searchError={searchError}
          onResetSearch={handleResetSearch} // ✅ new
          searchPlaceholder="Search Partnerss..."
          enableRowClick={true}
          onRowClick={handleRowClick}
          emptyStateMessage={error ? error : "No Partnerss found"}
          className="text-xs" // Add smaller text size class
        />
      </div>

      {/* Add Brand Dialog */}
      <Dialog
        isOpen={addBrandOpen}
        onClose={() => setAddBrandOpen(false)}
        title="Add New Partner/Brand"
        size="md"
      >
        <AddBrandForm
          onCancel={() => setAddBrandOpen(false)}
          onSubmit={handleCreateBrand}
        />
      </Dialog>

      {/* Brand Details Dialog */}
      <Dialog
        isOpen={detailsOpen}
        onClose={handleCloseDetails}
        title={isEditing ? "Edit" : (selectedBrand ? `${selectedBrand.name} Details` : "Partner Details")}
        size="lg"
      >
        {selectedBrand && editData && (
          <BrandDetailTabs
            selectedBrand={selectedBrand}
            setSelectedBrand={setSelectedBrand}
            editData={editData}
            isEditing={isEditing}
            activeDetailsTab={activeDetailsTab}
            setActiveDetailsTab={setActiveDetailsTab}
            errors={errors}
            handleChange={handleChange}
            isUpdating={isUpdating}
            handleSave={handleSave}
            setIsEditing={setIsEditing}
            canManageBrands={canManageBrands}
            isBankKycEditing={isBankKycEditing}
            isShopKycEditing={isShopKycEditing}
            isDocsKycEditing={isDocsKycEditing}
            bankKycData={bankKycData}
            shopKycData={shopKycData}
            docsKycFiles={docsKycFiles}
            handleEditBankKyc={handleEditBankKyc}
            handleEditShopKyc={handleEditShopKyc}
            handleEditDocsKyc={handleEditDocsKyc}
            handleBankKycChange={handleBankKycChange}
            handleShopKycChange={handleShopKycChange}
            handleFileChange={handleFileChange}
            setIsBankKycEditing={setIsBankKycEditing}
            setIsShopKycEditing={setIsShopKycEditing}
            setIsDocsKycEditing={setIsDocsKycEditing}
            saveBankKyc={saveBankKyc}
            saveShopKyc={saveShopKyc}
            saveDocsKyc={saveDocsKyc}
            handleDeleteConfirm={handleDeleteConfirm}
            setDocsKycFiles={setDocsKycFiles}
          />
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete"{selectedBrand?.name}"? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              isLoading={isDeleting}
              onClick={handleDeleteBrand}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ManageBrands; 